// Phase Z COMPLETE - ErrorBoundary fixed (removed design-system import)
// Root cause: @/design-system import in ErrorBoundary caused platform graph error
// HMR fix: force module rebuild - typography overhaul v2

import { Suspense, type ReactNode } from 'react';
import { logger } from '@/utils/logger';
import { BrowserRouter, Routes, Route, useParams, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/contexts/AuthContext';
import { LocaleProvider } from '@/contexts/LocaleContext';
import { LocalizationEditProvider } from '@/contexts/LocalizationEditContext';
import { GuestExitPreventionProvider } from '@/contexts/GuestExitPreventionContext';
import { UserContextProvider } from '@/contexts/UserContext';
import { AvatarProvider } from '@/contexts/AvatarContext';
import { FeedbackDialogProvider } from '@/contexts/FeedbackDialogContext';
import { ROUTES } from '@/constants';
import { UnifiedLayoutWrapper } from '@/components/shared/UnifiedLayoutWrapper';
import { PublicLayoutWrapper } from '@/components/PublicLayoutWrapper';
import { DirectRouteEntity } from '@/components/shared/DirectRouteEntity';
import { ScrollToTop } from '@/components/ScrollToTop';
import { Toaster } from '@/components/ui/sonner';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { RouteAwareAppFallback } from '@/components/skeletons';
import { LocalizationReadyGate } from '@/components/LocalizationReadyGate';
import { DeferredLanguageNavigator } from '@/lib/routing';
import { RouteEntityProvider } from '@/contexts/RouteEntityContext';
import { useI18nInitializer } from '@/hooks/useI18nInitializer';
import { useCrossDomainSession } from '@/hooks/useCrossDomainSession';
import { useSignupEmailConfirmation } from '@/hooks/useSignupEmailConfirmation';
import { useNewsletterTokenConfirmation } from '@/hooks/useNewsletterTokenConfirmation';
import { useNewsletterUnsubscribe } from '@/hooks/useNewsletterUnsubscribe';
import { DomainEnforcer } from '@/components/DomainEnforcer';
import PaymentCheckoutRedirect from '@/pages/public/PaymentCheckoutRedirect';
import { AdminReturnBanner } from '@/components/admin/AdminReturnBanner';
import { 
  LazyHomePage, 
  LazyExperts, 
  LazyHelpCenter,
  LazyTopicsListing,
  LazyTopicPage,
  LazyDeterministicRouter,
  LazyTwoSegmentDeterministicRouter,
  LazyAuth,
  LazyAdminDashboard,
  LazyAdminManagement,
  
  LazyAdminIntelligence,
  LazyAdminRoles,
  LazyAdminOpportunities,
  LazyAdminActivity,
  
  LazyAdminPageEditor,
  LazySystemEntityEditor,
  LazyTopicEditor,
  LazyDesignSystem,
  LazyAccountOverview,
  LazyAccountProfile,
  LazyAccountSettings,
  LazyAccountPerformance,
  LazyOpportunities,
  LazyAccountPublications as LazyAccountPublications,
  LazyPublicationEditor as LazyPublicationEditor,
  LazyAccountChats,
  LazyAccountChatProjects,
  // Membership pages now embedded in AccountSettings via MembershipContent
  LazyComments,
  LazyMedia,
  LazyExpertProfile,
  LazyUserRegister,
  LazyExplore,
  LazyFavorites,
  LazyVerifyOpportunityEmail,
  LazyResetPassword,
  LazyTaxOracleChatArea,
  LazyOAuthCallback,
  LazyPlansPage,
  LazyCheckoutPage,
  LazyCheckoutConfirmation,
  LazyNotFound
} from '@/App.lazy';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Route-aware page loader for Suspense boundaries
 * Shows skeleton matching the destination page (public vs account/admin)
 * This is the ONLY skeleton layer - no nested Suspense in layout wrappers
 */
function PageLoader() {
  return <RouteAwareAppFallback />;
}

/**
 * 301-equivalent redirect from /experts/:vanityUrl to /:vanityUrl
 * Profiles use flat canonical URLs (identity invariant).
 */
function ExpertProfileRedirect() {
  const { vanityUrl } = useParams<{ vanityUrl: string }>();
  if (!vanityUrl) return <Navigate to="/" replace />;
  return <Navigate to={`/${vanityUrl}`} replace />;
}

/**
 * Legacy /account/publications/edit/:publicationId URL — redirect to the canonical
 * /account/publications/:publicationId route. PublicationEditor handles both view
 * and edit modes internally based on role + editorial_status.
 */
function PublicationEditRedirect() {
  const { publicationId } = useParams<{ publicationId: string }>();
  if (!publicationId) return <Navigate to="/account/publications" replace />;
  return <Navigate to={`/account/publications/${publicationId}`} replace />;
}

/**
 * Mounts useI18nInitializer inside the provider tree so it can access React context.
 */
function I18nInitWrapper({ children }: { children: ReactNode }) {
  useI18nInitializer();
  useCrossDomainSession();
  return <>{children}</>;
}

/**
 * Handles signup confirmation links (?token_hash=…&type=signup) landing on
 * any page. Must live inside BrowserRouter (uses useNavigate).
 */
function SignupEmailConfirmationHandler() {
  useSignupEmailConfirmation();
  useNewsletterTokenConfirmation();
  useNewsletterUnsubscribe();
  return null;
}

export default function App() {
  if (import.meta.env.DEV) {
    logger.debug('[App] Root component rendering');
  }
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LocaleProvider>
          <RouteEntityProvider>
            <UserContextProvider>
              <I18nInitWrapper>
              <AvatarProvider>
                <FeedbackDialogProvider>
                   <BrowserRouter>
                    <DomainEnforcer />
                    <DeferredLanguageNavigator />
                    <GuestExitPreventionProvider>
                       <LocalizationEditProvider>
                         <ScrollToTop />
                         <SignupEmailConfirmationHandler />
                         <Toaster />
                        <AdminReturnBanner />
                  <Routes>
                {/* Auth routes - standalone without Header/Footer, but with localization */}
                <Route path={ROUTES.AUTH} element={
                  <LocalizationReadyGate>
                    <Suspense fallback={<PageLoader />}>
                      <LazyAuth />
                    </Suspense>
                  </LocalizationReadyGate>
                } />
                <Route path={ROUTES.AUTH_CALLBACK} element={
                  <Suspense fallback={<PageLoader />}>
                    <LazyOAuthCallback />
                  </Suspense>
                } />
                <Route path={ROUTES.RESET_PASSWORD} element={
                  <LocalizationReadyGate>
                    <Suspense fallback={<PageLoader />}>
                      <LazyResetPassword />
                    </Suspense>
                  </LocalizationReadyGate>
                } />

                {/* Public pages wrapped in PublicLayoutWrapper + LocalizationReadyGate */}
                <Route element={<LocalizationReadyGate><PublicLayoutWrapper /></LocalizationReadyGate>}>
                  <Route path={ROUTES.ROOT} element={
                    <DirectRouteEntity>
                      <Suspense fallback={<PageLoader />}>
                        <LazyHomePage />
                      </Suspense>
                    </DirectRouteEntity>
                  } />
                  <Route path={ROUTES.EXPERTS} element={
                    <DirectRouteEntity sourceId="experts">
                      <Suspense fallback={<PageLoader />}>
                        <LazyExperts />
                      </Suspense>
                    </DirectRouteEntity>
                  } />
                  <Route path={ROUTES.HELP_CENTER} element={
                    <DirectRouteEntity sourceId="help-center">
                      <Suspense fallback={<PageLoader />}>
                        <LazyHelpCenter />
                      </Suspense>
                    </DirectRouteEntity>
                  } />
                  <Route path={ROUTES.EXPLORE} element={
                    <DirectRouteEntity sourceId="explore">
                      <Suspense fallback={<PageLoader />}>
                        <LazyExplore />
                      </Suspense>
                    </DirectRouteEntity>
                  } />
                  <Route path={ROUTES.FAVORITES} element={
                    <DirectRouteEntity sourceId="favorites">
                      <Suspense fallback={<PageLoader />}>
                        <LazyFavorites />
                      </Suspense>
                    </DirectRouteEntity>
                  } />
                  <Route path={ROUTES.TOPICS} element={
                    <DirectRouteEntity sourceId="topics">
                      <Suspense fallback={<PageLoader />}>
                        <LazyTopicsListing />
                      </Suspense>
                    </DirectRouteEntity>
                  } />
                  {/* TopicPage sets its own route entity (dynamic topic) so language switches keep the topic */}
                  <Route path={ROUTES.TOPIC_PAGE} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyTopicPage />
                    </Suspense>
                  } />
                  <Route path={ROUTES.PLANS} element={
                    <DirectRouteEntity sourceId="plans">
                      <Suspense fallback={<PageLoader />}>
                        <LazyPlansPage />
                      </Suspense>
                    </DirectRouteEntity>
                  } />
                  <Route path={ROUTES.CHECKOUT} element={
                    <DirectRouteEntity sourceId="checkout">
                      <Suspense fallback={<PageLoader />}>
                        <LazyCheckoutPage />
                      </Suspense>
                    </DirectRouteEntity>
                  } />
                  <Route path={ROUTES.CHECKOUT_CONFIRMATION} element={
                    <DirectRouteEntity sourceId="checkout">
                      <Suspense fallback={<PageLoader />}>
                        <LazyCheckoutConfirmation />
                      </Suspense>
                    </DirectRouteEntity>
                  } />
                  <Route path="/payment/checkout/confirmation" element={<PaymentCheckoutRedirect />} />
                  
                  <Route path={ROUTES.EXPERT_REGISTER} element={
                    <DirectRouteEntity sourceId="expert-register">
                      <Suspense fallback={<PageLoader />}>
                        <LazyUserRegister />
                      </Suspense>
                    </DirectRouteEntity>
                  } />
                  <Route path={ROUTES.VERIFY_OPPORTUNITY_EMAIL} element={
                    <DirectRouteEntity sourceId="verify-email">
                      <Suspense fallback={<PageLoader />}>
                        <LazyVerifyOpportunityEmail />
                      </Suspense>
                    </DirectRouteEntity>
                  } />
                  {/* /experts/:vanityUrl → 301 redirect to canonical /{vanityUrl} */}
                  <Route path={ROUTES.EXPERT_PROFILE} element={<ExpertProfileRedirect />} />
                  {/* Dynamic resolvers - DB-first resolution */}
                  <Route path="/:segment1/:segment2" element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyTwoSegmentDeterministicRouter />
                    </Suspense>
                  } />
                  <Route path="/:slug" element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyDeterministicRouter />
                    </Suspense>
                  } />
                  {/* 404 for public routes - renders within Header/Footer layout */}
                  <Route path="*" element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyNotFound />
                    </Suspense>
                  } />
                </Route>

                {/* Chat routes - accessible to guests with sidebar */}
                <Route element={<UnifiedLayoutWrapper />}>
                  <Route path={ROUTES.CHAT} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyTaxOracleChatArea />
                    </Suspense>
                  } />
                  <Route path={ROUTES.CHAT_SESSION} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyTaxOracleChatArea />
                    </Suspense>
                  } />

                  {/* Account routes */}
                  <Route path={ROUTES.ACCOUNT} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAccountOverview />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ACCOUNT_PROFILE} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAccountProfile />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ACCOUNT_SETTINGS} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAccountSettings />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ACCOUNT_PERFORMANCE} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAccountPerformance />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ACCOUNT_OPPORTUNITIES} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyOpportunities />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ACCOUNT_PUBLICATIONS} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAccountPublications />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ACCOUNT_PUBLICATION_EDITOR} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyPublicationEditor />
                    </Suspense>
                  } />
                  {/* Legacy /edit/:publicationId URL → canonical /:publicationId */}
                  <Route
                    path="/account/publications/edit/:publicationId"
                    element={<PublicationEditRedirect />}
                  />
                  <Route path={ROUTES.ACCOUNT_CHATS} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAccountChats />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ACCOUNT_CHATS_PROJECTS} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAccountChatProjects />
                    </Suspense>
                  } />
                  {/* Membership routes removed - now handled via AccountSettings query params */}
                  <Route path={ROUTES.ACCOUNT_COMMENTS} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyComments />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ACCOUNT_MEDIA} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyMedia />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ACCOUNT_FAVORITES} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyFavorites />
                    </Suspense>
                  } />

                  {/* Admin routes */}
                  <Route path={ROUTES.ADMIN} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAdminDashboard />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ADMIN_OPERATIONS} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAdminDashboard />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ADMIN_OPERATIONS_HELP_CENTER} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAdminDashboard />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ADMIN_OPERATIONS_INDEXATION} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAdminDashboard />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ADMIN_OPERATIONS_ANALYTICS} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAdminDashboard />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ADMIN_MANAGEMENT} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAdminManagement />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ADMIN_ROLES} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAdminRoles />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ADMIN_ACTIVITY} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAdminActivity />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ADMIN_OPPORTUNITIES} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAdminOpportunities />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ADMIN_INTELLIGENCE} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAdminIntelligence />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ADMIN_MANAGEMENT_PAGE_EDITOR} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAdminPageEditor />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ADMIN_MANAGEMENT_PAGE_NEW} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyAdminPageEditor />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ADMIN_MANAGEMENT_ENTITY_EDITOR} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazySystemEntityEditor />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ADMIN_TOPIC_EDITOR} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyTopicEditor />
                    </Suspense>
                  } />
                  <Route path={ROUTES.ADMIN_DESIGN_SYSTEM} element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyDesignSystem />
                    </Suspense>
                  } />
                  {/* 404 for authenticated routes - renders within sidebar layout */}
                  <Route path="*" element={
                    <Suspense fallback={<PageLoader />}>
                      <LazyNotFound />
                    </Suspense>
                  } />
                </Route>

                {/* Global 404 fallback */}
                <Route path="*" element={
                  <Suspense fallback={<PageLoader />}>
                    <LazyNotFound />
                  </Suspense>
                } />
                    </Routes>
                  </LocalizationEditProvider>
                </GuestExitPreventionProvider>
              </BrowserRouter>
                </FeedbackDialogProvider>
              </AvatarProvider>
              </I18nInitWrapper>
            </UserContextProvider>
          </RouteEntityProvider>
        </LocaleProvider>
      </AuthProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}
